// The countdown shown in the mission header — CCT-288.
//
// ⚠️ IT TICKS ALONE, ON PURPOSE. Holding the "seconds left" state in ChapterDetailScreen would
// re-render that whole screen once per second — the timeline, the progression column, every
// card. This component is declared at MODULE level and owns its interval, so the tick stops
// here. (That is also why ChapterDetailScreen's panels are CALLED rather than mounted: a
// component redefined on each render remounts its subtree, and the progression column would
// jump back to the top every single second.)
//
// ⚠️ IT SUBTRACTS, IT DOES NOT COUNT. Every second it recomputes `deadline − now`. A component
// decrementing a counter would drift, and — worse — would be wrong after a backgrounded tab,
// where browsers throttle timers to once a minute. The instant is the source of truth.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, View, Text, StyleSheet } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { MissionTimer, secondesRestantes, formatChrono } from '../api/missionTimers';
import type { MessageKey } from '../i18n';

interface Props {
  timer: MissionTimer | null;
  /** Palette of the current theme — colours are not constants here (CCT-279). */
  colors: { textPrim: string; orange: string; red?: string };
  /** i18n, so the label follows the run's language like everything else. */
  t: (k: MessageKey, p?: any) => string;
}

/** Under a minute, the countdown turns urgent. */
const SEUIL_URGENCE_S = 60;
/** Below this threshold the countdown PULSES on top of changing colour. */
const SEUIL_CRITIQUE_S = 10;

export default function MissionCountdown({ timer, colors, t }: Props) {
  const [restant, setRestant] = useState(() => secondesRestantes(timer));

  useEffect(() => {
    if (!timer?.deadlineAt) return;
    // Recomputed from the deadline at every tick — never `restant - 1`.
    const maj = () => setRestant(secondesRestantes(timer));
    maj();
    const id = setInterval(maj, 1000);
    return () => clearInterval(id);
  }, [timer?.deadlineAt, timer?.elementId]);

  // ⚠️ THE PULSE IS A SECOND CHANNEL, NOT DECORATION. Under ten seconds a mere change of
  // shade says nothing to someone who cannot tell the two colours apart — and nothing at all
  // to someone not looking at the header right then. Movement, on the other hand, is caught
  // out of the corner of the eye.
  const pulse = useRef(new Animated.Value(1)).current;
  const critique = restant > 0 && restant <= SEUIL_CRITIQUE_S;
  useEffect(() => {
    if (!critique) { pulse.setValue(1); return; }
    const boucle = Animated.loop(Animated.sequence([
      Animated.timing(pulse, { toValue: 1.12, duration: 450, useNativeDriver: true }),
      Animated.timing(pulse, { toValue: 1, duration: 450, useNativeDriver: true }),
    ]));
    boucle.start();
    return () => boucle.stop();
  }, [critique, pulse]);

  if (!timer?.deadlineAt) return null;

  const urgent = restant <= SEUIL_URGENCE_S;
  const ecoule = restant <= 0;
  const teinte = ecoule ? (colors.red ?? colors.orange) : urgent ? colors.orange : colors.textPrim;
  // ELAPSED share of the allotted time, between 0 and 1 — this is the gauge.
  const avancement = timer.limitSeconds > 0
    ? Math.min(1, Math.max(0, 1 - restant / timer.limitSeconds))
    : 0;

  return (
    <Animated.View style={[s.wrap, { borderColor: teinte, transform: [{ scale: pulse }] }]}
      // Accessibility: a colour change alone would say nothing to a screen reader, and nothing
      // at all to a player who does not distinguish the two shades.
      accessibilityRole="timer"
      accessibilityLabel={ecoule
        ? t('chapter.timer.expired')
        : t('chapter.timer.remaining', { time: formatChrono(restant) })}
    >
      <Ionicons name={ecoule ? 'alert-circle' : urgent ? 'alarm' : 'timer-outline'}
        size={16} color={teinte} />
      <View>
        <View style={s.ligne}>
          <Text style={[s.texte, { color: teinte }]}>
            {ecoule ? t('chapter.timer.expired') : formatChrono(restant)}
          </Text>
          {timer.scope === 'mission' && !ecoule && (
            // WHICH clock is running changes what the player should do: a mission budget
            // and a quiz limit look alike, and they would rush the wrong one.
            <Text style={[s.portee, { color: teinte }]}>{t('chapter.timer.wholeMission')}</Text>
          )}
        </View>
        {/* GAUGE — the proportion reads without reading the digits, and without knowing how
            much time the author granted. "04:12" alone does not say whether that is a lot. */}
        <View style={s.jaugeFond}>
          <View style={[s.jaugeRemplie,
            { width: `${Math.round(avancement * 100)}%`, backgroundColor: teinte }]} />
        </View>
      </View>
    </Animated.View>
  );
}

const s = StyleSheet.create({
  wrap: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 4,
    borderRadius: 999, borderWidth: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  ligne: { flexDirection: 'row', alignItems: 'baseline', gap: 6 },
  // ⚠️ `tabular-nums`: without it the digits have different widths and the countdown JITTERS
  // every second. Something that twitches draws the eye for the wrong reason.
  texte: { fontSize: 16, fontWeight: '800', fontVariant: ['tabular-nums'], letterSpacing: 0.5 },
  portee: { fontSize: 10, opacity: 0.85 },
  jaugeFond: {
    height: 3, marginTop: 3, borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.18)', overflow: 'hidden',
  },
  jaugeRemplie: { height: 3, borderRadius: 2 },
});
